import { Box, Heading, IconButton, Input, Text, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { Add } from '@wix/wix-ui-icons-common/lazy'
import React, { FC, useCallback, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { Button } from '../../reusable/Button'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import type { Content } from './Content'
import { focusInput } from './focusInput'
import { SidePanel } from './SidePanel'
import { useInputs } from './useInputs'
import { useLoadEmail } from './useLoadEmail'
import { validateEmail, ValidateEmailReturn } from './validateEmail'

// Account Website Designer - https://bo.wix.com/wow/permissions/roles/6643122105421848577
const defaultRolePolicyId = '6643122105421848577'

export const WixStudioInvite: FC = () => {
  const [isPageLoading] = usePageLoading()
  const { save } = useStorageContext()

  const [isPrimaryButtonClicked, setPrimaryButtonClicked] = useState(false)

  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()

  const handleSkipClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    void pageLogger.skip()
    next({})
  }, [next, isPageLoading, pageLogger])

  const { inputs, inputsRef, onChange, onKeyDown, onAddMoreClick, onEnterPressed, validationMap, setValidationMap } =
    useInputs()

  const handleNextClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    const newValidationMap = inputs.reduce((acc, input, i) => {
      acc[i] = !input ? undefined : validateEmail(input)
      return acc
    }, {} as Record<string, ValidateEmailReturn>)

    const isInputsInvalid = Object.values(newValidationMap).some(value => value)

    if (isInputsInvalid) {
      setValidationMap(newValidationMap)
      return
    }

    setPrimaryButtonClicked(true)

    const accountInvites = inputs
      .filter(Boolean)
      .filter(input => !validateEmail(input))
      .map(input => ({ email: input, policyId: defaultRolePolicyId }))

    void pageLogger.answer({ answer: Boolean(accountInvites.length).toString() })
    save({ accountInvites })
    next({})
  }, [inputs, isPageLoading, pageLogger, save, next, setValidationMap])

  useEffect(() => {
    inputsRef.current && focusInput(inputsRef.current, 0)
  }, [inputsRef])

  const [emailState] = useLoadEmail()

  const sidePanelHeading = content.sidePanelTitle
  const validInputs = inputs.filter(Boolean).filter(input => !validateEmail(input))
  const sidePanelInvites = [
    { label: content.currentUserLabel, ...enrichYou(emailState) },
    ...validInputs.map(label => ({ label })),
  ]

  return (
    <WixStudioTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      title={content.title}
      subTitleBelow={content.lowerTitle}
      backButton={content.back}
      sidePanel={<SidePanel invites={sidePanelInvites} heading={sidePanelHeading} text={content.sidePanelText} />}
    >
      <Box direction="vertical" minHeight="456px">
        <Box margin="48px 0 18px">
          <Heading size="medium">{content.label}</Heading>
        </Box>
        <div ref={inputsRef}>
          <Box
            margin="0px -6px 6px"
            padding="6px"
            overflowY="scroll"
            direction="vertical"
            maxWidth="472px"
            gap="12px"
            maxHeight="240px"
            boxSizing="border-box"
          >
            {inputs.map((input, index) => {
              const validationKey = validationMap[index]
              return (
                <Box gap="6px" key={index}>
                  <Box direction="vertical" flex={1} maxWidth="400px">
                    <Input
                      value={input}
                      name={`${index}`}
                      type="email"
                      onChange={onChange}
                      placeholder={content.placeholder}
                      onKeyDown={onKeyDown}
                      onEnterPressed={onEnterPressed}
                      status={validationKey ? 'error' : undefined}
                      statusMessage={validationKey ? content[validationKey] : undefined}
                      tooltipPlacement="left"
                    />
                  </Box>
                  {index === inputs.length - 1 && (
                    <IconButton skin="light" onClick={onAddMoreClick}>
                      <Add />
                    </IconButton>
                  )}
                </Box>
              )
            })}
          </Box>
        </div>
        <Text size="small">
          <i style={{ color: '#868AA5' }}>{content.hint}</i>
        </Text>
        <Box marginTop="56px" direction="horizontal" gap="18px" verticalAlign="middle">
          <Button onClick={handleNextClick} isLoading={isPrimaryButtonClicked} label={content.next} skin="standard" />
          <TextButton underline="always" size="medium" weight="thin" onClick={handleSkipClick}>
            {content.skip}
          </TextButton>
        </Box>
      </Box>
    </WixStudioTemplate>
  )
}

const enrichYou = (emailState: ReturnType<typeof useLoadEmail>[0]) => {
  if (emailState.type === 'ready') {
    return { letter: emailState.email[0].toUpperCase() }
  }
  return { loading: true }
}
